import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Sun, Sprout, AlertTriangle, Eye, Heart, Flame, Cloud, Sparkles, X } from "lucide-react";
import { cn } from "@/lib/utils";

const THEME_ICONS = {
  joy: Sun,
  growth: Sprout,
  stress: AlertTriangle,
  reflection: Eye,
  connection: Heart,
  motivation: Flame,
  sadness: Cloud,
  creativity: Sparkles,
};

const THEME_COLORS = {
  joy: "#F59E0B",
  growth: "#10B981",
  stress: "#EF4444",
  reflection: "#6366F1",
  connection: "#EC4899",
  motivation: "#F97316",
  sadness: "#6B7280",
  creativity: "#8B5CF6",
};

const THEME_LABELS = {
  joy: "Joy",
  growth: "Growth",
  stress: "Stress",
  reflection: "Reflection",
  connection: "Connection",
  motivation: "Motivation",
  sadness: "Processing",
  creativity: "Creativity",
};

export function ThemeBadge({ themeKey, size = "sm" }) {
  const Icon = THEME_ICONS[themeKey] || Sparkles;
  const color = THEME_COLORS[themeKey] || "#6B7280";
  const label = THEME_LABELS[themeKey] || themeKey;

  if (size === "xs") {
    return (
      <span
        data-testid={`theme-badge-${themeKey}`}
        className="inline-flex items-center gap-0.5 px-1.5 py-px rounded-full text-[8px] font-medium"
        style={{ backgroundColor: color + "15", color }}
        title={label}
      >
        <Icon className="w-2 h-2" strokeWidth={2} />
        {label}
      </span>
    );
  }

  return (
    <span
      data-testid={`theme-badge-${themeKey}`}
      className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-medium"
      style={{ backgroundColor: color + "15", color }}
    >
      <Icon className="w-3 h-3" strokeWidth={2} />
      {label}
    </span>
  );
}

export function ThemeFilter({ themes, activeTheme, onSelect, onClear }) {
  if (!themes || themes.length === 0) return null;

  const uniqueThemes = [...new Set(themes)];

  return (
    <div data-testid="theme-filter" className="flex items-center gap-1 flex-wrap">
      {uniqueThemes.map(key => {
        const Icon = THEME_ICONS[key] || Sparkles;
        const color = THEME_COLORS[key] || "#6B7280";
        const label = THEME_LABELS[key] || key;
        const isActive = activeTheme === key;

        return (
          <button
            key={key}
            data-testid={`theme-filter-${key}`}
            onClick={() => onSelect(isActive ? null : key)}
            className={cn(
              "inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium transition-all border",
              isActive
                ? "shadow-sm"
                : "border-transparent hover:shadow-sm"
            )}
            style={{
              backgroundColor: isActive ? color + "20" : color + "08",
              color: color,
              borderColor: isActive ? color + "40" : "transparent",
            }}
          >
            <Icon className="w-2.5 h-2.5" strokeWidth={2} />
            {label}
          </button>
        );
      })}
      {activeTheme && (
        <button
          onClick={() => onClear()}
          className="px-1.5 py-0.5 text-[10px] text-muted-foreground hover:text-foreground"
        >
          Clear
        </button>
      )}
    </div>
  );
}

export function useConversationThemes() {
  const [allThemes, setAllThemes] = useState([]);

  const collectThemes = (conversations) => {
    const themeSet = new Set();
    conversations.forEach(c => {
      (c.themes || []).forEach(t => themeSet.add(t));
    });
    setAllThemes([...themeSet]);
  };

  return { allThemes, collectThemes };
}
